import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useEffect, useState } from "react";
import { useCart } from "../lib/CartContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Wallet, CreditCard, Banknote, Smartphone, Info } from "lucide-react";
import OrderConfirmation from "./OrderConfirmation";
import { useLang } from "../lib/i18n";

const PAYMENT_KEYS = [
  { key: "Vodafone Cash", icon: Smartphone, color: "#E60012" },
  { key: "Instapay", icon: Wallet, color: "#0F62FE" },
  { key: "FawryPay", icon: CreditCard, color: "#FFB800" },
  { key: "Cash on Delivery", icon: Banknote, color: "#34D399" },
];

export default function CheckoutDialog({ open, onOpenChange, shippingMethod, shippingFee, discountCode = "", discountPercent = 0, discountAmount = 0 }) {
  const { items, subtotal, clear, setOpen: setCartOpen } = useCart();
  const { t } = useLang();
  const total = subtotal - discountAmount + (items.length ? shippingFee : 0);
  const MIN_ORDER_VALUE = 200;
  const eligibleSubtotal = Math.round((subtotal - discountAmount) * 100) / 100;
  const belowMinimum = items.length > 0 && eligibleSubtotal < MIN_ORDER_VALUE;
  const remainingForMin = Math.max(0, Math.round((MIN_ORDER_VALUE - eligibleSubtotal) * 100) / 100);
  const remainingLabel = remainingForMin % 1 === 0 ? remainingForMin.toFixed(0) : remainingForMin.toFixed(2);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState({
    payment_phone: "01024049798",
    vodafone_cash_phone: "01042192135",
    fawry_wallet_phone: "01024049798",
    fawry_reference: "FAWRY_REFERENCE_CODE_PLACEHOLDER",
  });
  const [form, setForm] = useState({
    full_name: "", phone: "", whatsapp: "", email: "", city: "", area: "",
    address: "", address_notes: "", delivery_time: "", order_notes: "",
    payment_method: "Cash on Delivery", payment_reference: "",
  });

  useEffect(() => {
    api.get("/payment-info").then((r) => setPaymentInfo(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!items.length) {
      setError("Your cart is empty.");
      return;
    }
    if (belowMinimum) {
      setError(t("cart.min_order_message", { amount: remainingLabel }));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        shipping_method: shippingMethod,
        discount_code: discountCode || "",
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, icon: i.icon, color: i.color,
        })),
      };
      const { data } = await api.post("/orders", payload);
      setConfirmation(data);
      clear();
      setCartOpen(false);
      toast.success(t("checkout.placed_toast"));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <OrderConfirmation
        order={confirmation}
        paymentInfo={paymentInfo}
        open={true}
        onClose={() => {
          setConfirmation(null);
          onOpenChange(false);
        }}
      />
    );
  }

  const renderInstructions = () => {
    const m = form.payment_method;
    const amount = total.toFixed(0);
    const vodafonePhone = paymentInfo.vodafone_cash_phone;
    const instapayPhone = paymentInfo.payment_phone;
    const fawryPhone = paymentInfo.fawry_wallet_phone;
    if (m === "Vodafone Cash") {
      return { line1: t("checkout.vodafone_inst", { amount, phone: vodafonePhone }), line2: t("checkout.vodafone_step") };
    }
    if (m === "Instapay") {
      return { line1: t("checkout.instapay_inst", { amount, phone: instapayPhone }), line2: t("checkout.instapay_step") };
    }
    if (m === "FawryPay") {
      return { line1: t("checkout.fawry_inst", { amount, phone: fawryPhone }), line2: t("checkout.fawry_step") };
    }
    return { line1: t("checkout.cod_inst", { amount }), line2: "" };
  };

  const inst = renderInstructions();
  const showRefField = form.payment_method !== "Cash on Delivery";
  const refLabel = t("checkout.ref_phone");
  const refPlaceholder = t("checkout.ref_phone_ph");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-auto"
        style={{ background: "#FFFBF2", border: "2px solid #0F172A", borderRadius: 20 }}
        data-testid="checkout-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{t("checkout.title")}</DialogTitle>
          <DialogDescription>Fill in your delivery and contact details to place the order.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2" data-testid="checkout-form">
          <Field label={t("checkout.full_name")} full={false}>
            <Input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="input-full_name" />
          </Field>
          <Field label={t("checkout.phone")}>
            <Input required value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="input-phone" />
          </Field>
          <Field label={t("checkout.whatsapp")}>
            <Input value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} data-testid="input-whatsapp" />
          </Field>
          <Field label={t("checkout.email")}>
            <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="input-email" />
          </Field>
          <Field label={t("checkout.city")}>
            <Input required value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="input-city" />
          </Field>
          <Field label={t("checkout.area")}>
            <Input required value={form.area} onChange={(e) => update("area", e.target.value)} data-testid="input-area" />
          </Field>
          <Field label={t("checkout.address")} full>
            <Input required value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="input-address" />
          </Field>
          <Field label={t("checkout.address_notes")} full>
            <Input value={form.address_notes} onChange={(e) => update("address_notes", e.target.value)} data-testid="input-address_notes" />
          </Field>
          <Field label={t("checkout.delivery_time")}>
            <Input placeholder={t("checkout.delivery_ph")} value={form.delivery_time} onChange={(e) => update("delivery_time", e.target.value)} data-testid="input-delivery_time" />
          </Field>
          <Field label={t("checkout.order_notes")} full>
            <Textarea rows={2} value={form.order_notes} onChange={(e) => update("order_notes", e.target.value)} data-testid="input-order_notes" />
          </Field>

          <div className="sm:col-span-2 mt-1">
            <Label className="font-display text-sm font-semibold mb-2 block">{t("checkout.payment_method")}</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="payment-methods">
              {PAYMENT_KEYS.map((p) => {
                const Icon = p.icon;
                const active = form.payment_method === p.key;
                const disabled = !!p.comingSoon;
                return (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => !disabled && update("payment_method", p.key)}
                    disabled={disabled}
                    aria-disabled={disabled}
                    data-testid={`payment-${p.key.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`neo-flat p-3 flex items-center gap-3 text-start ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                    style={active ? { background: "#FDE047" } : { background: "#fff" }}
                  >
                    <span
                      className="w-8 h-8 rounded-md flex items-center justify-center text-white shrink-0"
                      style={{ background: p.color, border: "2px solid #0F172A" }}
                    >
                      <Icon size={16} />
                    </span>
                    <span className="font-semibold text-sm flex-1">{t(`payments.${p.key}`)}</span>
                    {disabled && (
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: "#0F172A", color: "#FDE047" }}
                        data-testid={`payment-${p.key.replace(/\s+/g, "-").toLowerCase()}-coming-soon`}
                      >
                        {t("payments.coming_soon")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Payment instructions panel */}
          <div
            className="sm:col-span-2 neo-flat p-4 flex gap-3"
            style={{ background: "#FDE047", border: "2px solid #0F172A" }}
            data-testid="payment-instructions"
          >
            <Info size={20} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-display font-bold mb-1">{t("checkout.pay_inst_title")}</div>
              <div>{inst.line1}</div>
              {inst.line2 && <div className="mt-1 text-slate-700">{inst.line2}</div>}
            </div>
          </div>

          {showRefField && (
            <Field label={refLabel} full>
              <Input
                value={form.payment_reference}
                onChange={(e) => update("payment_reference", e.target.value)}
                placeholder={refPlaceholder}
                data-testid="input-payment_reference"
              />
              <span className="text-xs text-slate-500 mt-1">{t("checkout.payment_reference_optional")}</span>
            </Field>
          )}

          <div className="sm:col-span-2 neo-flat p-3 bg-white">
            <Row label={t("checkout.subtotal")} value={`${subtotal.toFixed(0)} EGP`} />
            {discountPercent > 0 && (
              <Row label={`${discountCode} (${discountPercent}%)`} value={`- ${discountAmount.toFixed(0)} EGP`} />
            )}
            <Row label={`${t("checkout.ship_label")} (${shippingMethod})`} value={`${shippingFee} EGP`} />
            <div className="flex justify-between font-display text-lg font-bold pt-2 border-t border-dashed border-slate-300 mt-2">
              <span>{t("checkout.total")}</span>
              <span data-testid="checkout-total">{total.toFixed(0)} EGP</span>
            </div>
          </div>

          {belowMinimum && (
            <div className="sm:col-span-2 text-sm font-semibold px-3 py-2 rounded-lg flex items-start gap-2"
                 style={{ background: "#FEF3C7", border: "2px solid #0F172A", color: "#92400E" }}
                 data-testid="checkout-min-order-message">
              {t("cart.min_order_message", { amount: remainingLabel })}
            </div>
          )}

          {error && (
            <div className="sm:col-span-2 text-sm font-semibold text-red-600" data-testid="checkout-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting || items.length === 0 || belowMinimum}
            className="neo-btn sm:col-span-2 py-3 font-bold text-white disabled:opacity-60"
            style={{ background: "var(--pm-dog)" }}
            data-testid="submit-order-btn"
          >
            {submitting ? t("checkout.submitting") : t("checkout.submit")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-sm font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
